import React from 'react';
import styled from 'styled-components';
import {useComponent} from '../../component-library';
import {Table} from './table';
import {TwoColumnHeadline} from './two-column-headline';
import {EfficiencyClassTable} from './efficiency-class-table';
import {EfficiencyEntryRow} from './efficiency-entry-row';
import {
  MeasurementStandardEfficiencyDetails,
  EfficiencyTranslations,
} from '@volkswagen-onehub/whitelabel-e-mobility-data-types';

interface MeasurementStandardDetailsProps {
  disclaimerReferenceLabelTranslation: string | undefined;
  efficiencyTranslations: EfficiencyTranslations;
  details: MeasurementStandardEfficiencyDetails;
  closeButtonLabel: string;
  includeDisclaimerInEfficiencyLayer: boolean;
}

export function MeasurementStandardDetails({
  disclaimerReferenceLabelTranslation,
  efficiencyTranslations,
  details,
  closeButtonLabel,
  includeDisclaimerInEfficiencyLayer,
}: MeasurementStandardDetailsProps): JSX.Element {
  const {efficiencyClass, fuelTypeEfficiencyDetailsList, infoLink} = details;

  const {
    efficiencyClassDisclaimers,
    efficiencyClassDisclaimer: configEfficiencyClassDisclaimer,
    consumptionEmissionDisclaimers,
    consumptionEmissionDisclaimer: configConsumptionEmissionDisclaimer,
    layerSubHeadlines,
    layerSubHeadline: configLayerSubHeadline,
    electricRangeDisclaimer,
  } = efficiencyTranslations;

  const efficiencyClassDisclaimer = configEfficiencyClassDisclaimer
    ? configEfficiencyClassDisclaimer
    : efficiencyClassDisclaimers && efficiencyClassDisclaimers['WLTP'];

  const consumptionEmissionDisclaimer = configConsumptionEmissionDisclaimer
    ? configConsumptionEmissionDisclaimer
    : consumptionEmissionDisclaimers && consumptionEmissionDisclaimers['WLTP'];

  const Text = useComponent('Text');
  const Link = useComponent('Link');

  // TODO: In 332 will be made mandatory again
  const layerSubHeadline = configLayerSubHeadline
    ? configLayerSubHeadline
    : layerSubHeadlines
    ? layerSubHeadlines['WLTP']
    : '';

  return (
    <>
      <TwoColumnHeadline>
        {infoLink ? (
          <MeasurementStandardWrapperWithLink>
            <Text tag="h2" variant="headline-small" bold>
              {layerSubHeadline}
            </Text>
          </MeasurementStandardWrapperWithLink>
        ) : (
          <MeasurementStandardWrapper>
            <Text tag="h2" variant="headline-small" bold>
              {layerSubHeadline}
            </Text>
          </MeasurementStandardWrapper>
        )}
        {infoLink && (
          <Text variant="label" tag="span">
            <InfoLinkWrapper>
              <Link href={infoLink.url} ariaLabel={infoLink.ariaLabel}>
                {infoLink.label}
              </Link>
            </InfoLinkWrapper>
          </Text>
        )}
      </TwoColumnHeadline>
      {fuelTypeEfficiencyDetailsList &&
        fuelTypeEfficiencyDetailsList.map(
          ({fuelTypeLabel, consumptions, emissions = [], electricRanges}) => (
            <div key={fuelTypeLabel}>
              <Text tag="span" variant="label" bold>
                {fuelTypeLabel}
              </Text>
              <Table>
                {[...consumptions, ...emissions].map(
                  (entry) =>
                    entry.label && (
                      <EfficiencyEntryRow
                        key={entry.label}
                        entry={entry}
                        disclaimer={consumptionEmissionDisclaimer}
                        closeButtonLabel={closeButtonLabel}
                        disclaimerReferenceLabelTranslation={
                          disclaimerReferenceLabelTranslation
                        }
                        includeDisclaimerInEfficiencyLayer={
                          includeDisclaimerInEfficiencyLayer
                        }
                      />
                    )
                )}
                {electricRanges &&
                  electricRanges.map((entry) => (
                    <EfficiencyEntryRow
                      key={entry.label}
                      entry={entry}
                      disclaimer={electricRangeDisclaimer}
                      closeButtonLabel={closeButtonLabel}
                      disclaimerReferenceLabelTranslation={
                        disclaimerReferenceLabelTranslation
                      }
                      includeDisclaimerInEfficiencyLayer={
                        includeDisclaimerInEfficiencyLayer
                      }
                    />
                  ))}
              </Table>
            </div>
          )
        )}
      {efficiencyClass && (
        <EfficiencyClassTable
          label={efficiencyClass.label}
          value={efficiencyClass.value}
          disclaimer={efficiencyClassDisclaimer}
          closeButtonLabel={closeButtonLabel}
          disclaimerReferenceLabelTranslation={
            disclaimerReferenceLabelTranslation
          }
        />
      )}
    </>
  );
}

const MeasurementStandardWrapper = styled.div`
  padding: ${(props) => props.theme.internal.size.static350} 0;
`;

const MeasurementStandardWrapperWithLink = styled.div`
  padding-top: ${(props) => props.theme.internal.size.static350};
  padding-bottom: 0;

  @media (min-width: ${(props) => props.theme.internal.Breakpoints.b960}px) {
    padding: ${(props) => props.theme.internal.size.static350} 0;
  }
`;

const InfoLinkWrapper = styled.div`
  padding-top: 0;
  padding-bottom: ${(props) => props.theme.internal.size.static350};

  @media (min-width: ${(props) => props.theme.internal.Breakpoints.b960}px) {
    padding-top: ${(props) => props.theme.internal.size.static400};
    padding-bottom: 0;
  }
`;
